// frontend/src/pages/admin/ReportsPage/ReportsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import { generateReportApi, getReportsApi, downloadReportApi } from '../../../api/report.api';
import { getClubsApi } from '../../../api/club.api';
import { REPORT_TYPE_LABELS, AGE_GROUP_LABELS } from '../../../utils/constants';

const emptyForm = { title: '', type: 'PLAYER_LIST', format: 'excel', clubId: '', ageGroup: '', from: '', to: '' };

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [filters, setFilters] = useState({ type: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadClubs = useCallback(async () => {
    try {
      const { data } = await getClubsApi({ limit: 100 });
      setClubs(data.data);
    } catch {}
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getReportsApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setReports(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت گزارش‌ها');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadClubs(); }, []);
  useEffect(() => { loadReports(); }, [loadReports]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setGenerating(true);
    try {
      const params = {};
      if (form.clubId) params.clubId = Number(form.clubId);
      if (form.ageGroup) params.ageGroup = form.ageGroup;
      if (form.from) params.from = form.from;
      if (form.to) params.to = form.to;
      await generateReportApi({
        title: form.title,
        type: form.type,
        format: form.format,
        parameters: params,
      });
      setSnackbar('گزارش تولید شد');
      setFormOpen(false);
      setForm(emptyForm);
      loadReports();
    } catch (err) {
      setError(err.response?.data?.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (report) => {
    try {
      const ext = report.fileUrl.split('.').pop();
      await downloadReportApi(report.id, `${report.title}.${ext}`);
    } catch (err) {
      setSnackbar('دانلود ناموفق');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">گزارش‌گیری</h1>
        <Button variant="primary" onClick={() => setFormOpen(true)}>
          <Icon name="plus" className="ml-2 h-4 w-4" /> گزارش جدید
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        >
          <option value="">همه انواع</option>
          {Object.entries(REPORT_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 overflow-x-auto">
        {loading ? (
          <div className="py-8 text-center text-text-dark/50">در حال بارگذاری...</div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="bg-slate-50 text-text-dark/70">
                <tr>
                  <th className="px-4 py-3 font-medium">عنوان</th>
                  <th className="px-4 py-3 font-medium">نوع</th>
                  <th className="px-4 py-3 font-medium">تولیدکننده</th>
                  <th className="px-4 py-3 font-medium">تاریخ</th>
                  <th className="px-4 py-3 text-center font-medium">دانلود</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3">{r.title}</td>
                    <td className="px-4 py-3">{REPORT_TYPE_LABELS[r.type]}</td>
                    <td className="px-4 py-3">{r.generator?.fullName}</td>
                    <td className="px-4 py-3">{new Date(r.createdAt).toLocaleDateString('fa-IR')}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleDownload(r)} className="text-link hover:text-linkHover p-1">
                        <Icon name="download" className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {reports.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-text-dark/40">
                      گزارشی ثبت نشده است
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="تولید گزارش" size="lg">
        <form onSubmit={handleGenerate} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <Input label="عنوان گزارش" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <div className="grid grid-cols-2 gap-3">
            <select
              className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {Object.entries(REPORT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value })}
            >
              <option value="excel">اکسل</option>
              <option value="pdf">PDF</option>
            </select>
          </div>

          {(form.type === 'PLAYER_LIST' || form.type === 'FINANCIAL') && (
            <select
              className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
              value={form.clubId}
              onChange={(e) => setForm({ ...form, clubId: e.target.value })}
            >
              <option value="">همه باشگاه‌ها</option>
              {clubs.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          {form.type === 'PLAYER_LIST' && (
            <select
              className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
              value={form.ageGroup}
              onChange={(e) => setForm({ ...form, ageGroup: e.target.value })}
            >
              <option value="">همه گروه‌ها</option>
              {Object.entries(AGE_GROUP_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          )}

          {(form.type === 'FINANCIAL' || form.type === 'MATCH_SUMMARY') && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="از تاریخ" type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
              <Input label="تا تاریخ" type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setFormOpen(false)}>انصراف</Button>
            <Button variant="primary" type="submit" disabled={generating}>
              {generating ? 'در حال تولید...' : 'تولید گزارش'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}